/**
 * The "Quality" view of one exploration: the contract, the 14 tier-1 KPIs, and the drill-down.
 *
 * Four rules from `.agent_docs/graph-kpi-tier1.md` are enforced here, each pinned by a test:
 *
 *  1. A KPI with `value === null` renders its REASON as text. Never 0, never 0%, never "-", never an
 *     empty tile: on screen a zero and a "not computed" are indistinguishable, and the zero always
 *     gets read as a measurement.
 *  2. The global score is a CONTRACT, "N / M satisfied", with `unknown` shown SEPARATELY. Never a
 *     percentage of a weighted average: a weight is a free parameter nobody can defend.
 *  3. A tile with `rowCount > 0` opens its rows. `freshness` has `rowCount === null` and is inert.
 *  4. A ratio shows both terms, so 1/1 is distinguishable from 900/900.
 *  6. A violated invariant links to the KPI it reads, so a verdict reaches the rows in one click.
 */
import { useCallback, useRef, useState } from 'react';
import {
  Button,
  InlineLoading,
  InlineNotification,
  Tag,
  Tile,
} from '@carbon/react';
import { ChevronDown, ChevronUp, Renew } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { explorationApi } from '@/lib/api/exploration.api';
import type { GraphInvariant, GraphKpiId, GraphKpiReport, GraphKpiValue } from '@snxt/snxt-onto-share';
import {
  formatDetailKey,
  formatKpiTerms,
  formatKpiValue,
  hasDrillDown,
  invariantStatusLabel,
  kpiTone,
  polarityHint,
  toneTagType,
} from './kpiDisplay';
import { KpiRowsTable } from './KpiRowsTable';
import './GraphQualityPanel.scss';

interface GraphQualityPanelProps {
  explorationId: string;
}

/** Carbon tag colour for an invariant verdict. `unknown` is grey: it is neither a pass nor a fail. */
function invariantTagType(status: GraphInvariant['status']): 'green' | 'red' | 'gray' {
  if (status === 'satisfied') return 'green';
  if (status === 'violated') return 'red';
  return 'gray';
}

export function GraphQualityPanel({ explorationId }: GraphQualityPanelProps) {
  const [openKpi, setOpenKpi] = useState<GraphKpiId | null>(null);
  const tileRefs = useRef<Partial<Record<GraphKpiId, HTMLDivElement | null>>>({});

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<GraphKpiReport>({
    queryKey: ['graph-kpis', explorationId],
    queryFn: () => explorationApi.getKpis(explorationId),
  });

  const toggleKpi = useCallback((kpiId: GraphKpiId) => {
    setOpenKpi((current) => (current === kpiId ? null : kpiId));
  }, []);

  /**
   * Rule 6: the verdict reaches the offending rows in one click.
   *
   * Opening the drill-down is the behaviour; scrolling to it is a convenience, so it is guarded. It
   * is not defined in jsdom and it is not universal in browsers either, and an exception here would
   * take the whole panel down with it for a nicety.
   */
  const jumpToKpi = useCallback((kpiId: GraphKpiId) => {
    setOpenKpi(kpiId);
    const tile = tileRefs.current[kpiId];
    if (tile && typeof tile.scrollIntoView === 'function') {
      tile.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  if (isLoading) {
    return (
      <div className="graph-quality__state">
        <InlineLoading description="Computing quality KPIs…" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="graph-quality__state">
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Failed to load quality KPIs"
          subtitle={(error as Error)?.message}
        />
      </div>
    );
  }

  const { contract, kpis, populated } = data;

  return (
    <div className="graph-quality" data-testid="graph-quality-panel">
      {/* ── Contract ─────────────────────────────────────────────────────────── */}
      <section className="graph-quality__contract" data-testid="graph-quality-contract">
        <div className="graph-quality__contract-head">
          <div>
            <h3 className="graph-quality__contract-title">Quality contract</h3>
            <p className="graph-quality__contract-sub">
              Invariants that hold, counted. Not a weighted score: a weight is a number nobody can
              defend, so this is a count of checks, and the ones we cannot decide are listed apart.
            </p>
          </div>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Renew}
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {isFetching ? 'Recomputing…' : 'Recompute'}
          </Button>
        </div>

        <div className="graph-quality__score">
          {/* Rule 2: "N / M satisfied", never a percentage. */}
          <span className="graph-quality__score-value" data-testid="contract-score">
            {contract.satisfied} / {contract.total}
          </span>
          <span className="graph-quality__score-label">satisfied</span>
          {/* `unknown` is reported SEPARATELY and excluded from the denominator above, so the score
              can never improve by not knowing. */}
          <Tag type="gray" size="sm" data-testid="contract-unknown">
            {contract.unknown} not measured
          </Tag>
          {!populated && (
            <Tag type="magenta" size="sm" data-testid="contract-not-populated">
              projection never completed
            </Tag>
          )}
        </div>

        <ul className="graph-quality__invariants">
          {contract.invariants.map((inv) => (
            <li key={inv.id} className="graph-quality__invariant" data-testid={`invariant-${inv.id}`}>
              <Tag type={invariantTagType(inv.status)} size="sm">
                {invariantStatusLabel(inv.status)}
              </Tag>
              <span className="graph-quality__invariant-label">{inv.label}</span>
              <span className="graph-quality__invariant-expectation">{inv.expectation}</span>
              {inv.status === 'violated' && (
                <Button
                  kind="ghost"
                  size="sm"
                  data-testid={`invariant-link-${inv.id}`}
                  onClick={() => jumpToKpi(inv.kpiId)}
                >
                  See rows
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* ── KPI tiles ────────────────────────────────────────────────────────── */}
      <section className="graph-quality__kpis">
        {kpis.map((kpi) => (
          <KpiTile
            key={kpi.id}
            kpi={kpi}
            explorationId={explorationId}
            isOpen={openKpi === kpi.id}
            onToggle={toggleKpi}
            registerRef={(el) => {
              tileRefs.current[kpi.id] = el;
            }}
          />
        ))}
      </section>
    </div>
  );
}

interface KpiTileProps {
  kpi: GraphKpiValue;
  explorationId: string;
  isOpen: boolean;
  onToggle: (id: GraphKpiId) => void;
  registerRef: (el: HTMLDivElement | null) => void;
}

function KpiTile({ kpi, explorationId, isOpen, onToggle, registerRef }: KpiTileProps) {
  const clickable = hasDrillDown(kpi);
  const tone = kpiTone(kpi);
  const terms = formatKpiTerms(kpi);
  const isUnknown = kpi.value === null;

  return (
    <div className="graph-quality__kpi" ref={registerRef} data-testid={`kpi-${kpi.id}`}>
      <Tile className="graph-quality__kpi-tile">
        <div className="graph-quality__kpi-head">
          <span className="graph-quality__kpi-label">{kpi.label}</span>
          <Tag type={toneTagType(tone)} size="sm">
            {polarityHint(kpi.polarity)}
          </Tag>
        </div>

        {/* Rule 1: a missing value renders as its reason, as TEXT. The `data-kpi-unknown` attribute
            exists so a test can assert the distinction without depending on the wording. */}
        <div
          className={`graph-quality__kpi-value graph-quality__kpi-value--${tone ?? 'unknown'}`}
          data-testid={`kpi-value-${kpi.id}`}
          data-kpi-unknown={isUnknown ? 'true' : 'false'}
        >
          {formatKpiValue(kpi)}
        </div>

        {/* Rule 4: both terms of the ratio, so 1/1 is not read as 900/900. */}
        {terms && (
          <div className="graph-quality__kpi-terms" data-testid={`kpi-terms-${kpi.id}`}>
            {terms}
          </div>
        )}

        {kpi.detail && Object.keys(kpi.detail).length > 0 && (
          <div className="graph-quality__kpi-detail" data-testid={`kpi-detail-${kpi.id}`}>
            {Object.entries(kpi.detail).map(([key, value]) => (
              <span key={key}>
                {formatDetailKey(key)}: {value === null ? 'not computed' : value}
              </span>
            ))}
          </div>
        )}

        <p className="graph-quality__kpi-description">{kpi.description}</p>

        {/* Rule 3: clickable only when there is a set to open. */}
        {clickable ? (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={isOpen ? ChevronUp : ChevronDown}
            data-testid={`kpi-drilldown-${kpi.id}`}
            onClick={() => onToggle(kpi.id)}
          >
            {isOpen ? 'Hide' : `${kpi.rowCount} to review`}
          </Button>
        ) : (
          <span className="graph-quality__kpi-norows" data-testid={`kpi-norows-${kpi.id}`}>
            {isUnknown ? 'nothing to list' : 'no rows to review'}
          </span>
        )}
      </Tile>

      {isOpen && clickable && (
        <KpiRowsTable
          explorationId={explorationId}
          kpiId={kpi.id}
          expectedTotal={kpi.rowCount ?? 0}
        />
      )}
    </div>
  );
}

// Made with Bob
